import React, { useRef } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { $getRoot } from 'lexical';

import { CCMentionNode, CCMentionNodeReplacement } from './nodes/BeautifulMention';
import { ColorTextNode } from './nodes/ColorTextNode';
import { serializeToWire } from './serialize';
import EditorRefPlugin from './plugins/EditorRefPlugin';
import EnterSendPlugin from './plugins/EnterSendPlugin';
import ImagePastePlugin from './plugins/ImagePastePlugin';
import MentionPlugin from './plugins/MentionPlugin';
import QuickReplyPlugin from './plugins/QuickReplyPlugin';
import FloatingToolbarPlugin from './plugins/FloatingToolbarPlugin';
import { useComposer } from './ComposerContext';

// Maps Lexical's built-in format flags to CSS classes so bold/italic/underline
// render LIVE in the composer as you type.
const EDITOR_THEME = {
  paragraph: 'composer-paragraph',
  text: {
    bold: 'composer-bold',
    italic: 'composer-italic',
    underline: 'composer-underline',
  },
};

/**
 * The Lexical-backed message composer. Replaces the old MUI TextField. All
 * styling/chrome stays in ChatPanel; this owns only the editable surface and
 * the editor plugins. It talks to ChatPanel through:
 *   - apiRef: imperative handle (serialize/clear/focus/insertText/applyFormat/applyColor/hasSelection)
 *   - onTyping(wire): fired when the serialized content changes (presence)
 *   - onSend(): Enter pressed (ChatPanel reads apiRef.serialize() and sends)
 *   - onImagePaste(file): pasted image -> ChatPanel.stageImage
 *     (stageImage does HEIC conversion, validation, and inline error reporting;
 *     desktop drag-and-drop is handled at the chat-pane level in ChatPanel)
 *   - onSelectionRect(rect|null): drives ChatPanel's floating format toolbar
 *   - onlineUsers/currentUserId: mention autocomplete source
 *   - quickReplies: the user's saved snippets for the `/` slash typeahead
 *   - classes/cx: ChatPanel's tss-react styles, reused for the mention popover
 */
export default function MessageEditor({
  apiRef,
  onTyping,
  onSend,
  onImagePaste,
  onSelectionRect,
  onlineUsers,
  currentUserId,
  quickReplies,
  placeholder,
  editorMaxHeight,
  classes,
  cx,
}) {
  const lastWireRef = useRef('');
  // Hand the editor's imperative API to the ComposerContext so out-of-tree
  // callers (RightSidebar's "Mention") can insert into this editor.
  const { registerComposer } = useComposer();

  const initialConfig = {
    namespace: 'chat-composer',
    theme: EDITOR_THEME,
    nodes: [CCMentionNode, CCMentionNodeReplacement, ColorTextNode],
    onError(error) {
      // Don't let an editor error blank the whole chat; log and continue.
      // eslint-disable-next-line no-console
      console.error('Lexical composer error:', error);
    },
  };

  const handleChange = (editorState) => {
    const wire = editorState.read(() => serializeToWire($getRoot()));
    // Guard onTyping against caret-only updates that don't change the content.
    if (wire !== lastWireRef.current) {
      lastWireRef.current = wire;
      if (onTyping) onTyping(wire);
    }
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className={classes.editorShell}>
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              className={classes.editorContentEditable}
              aria-label="Message"
              // When the composer has been user-resized, let the editable fill
              // the taller pill (overrides the default ~4-line cap in the class).
              style={editorMaxHeight ? { maxHeight: editorMaxHeight } : undefined}
            />
          }
          placeholder={<div className={classes.editorPlaceholder}>{placeholder}</div>}
          ErrorBoundary={LexicalErrorBoundary}
        />
      </div>
      <HistoryPlugin />
      <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
      <EnterSendPlugin onSend={onSend} />
      <ImagePastePlugin onImage={onImagePaste} />
      <MentionPlugin
        onlineUsers={onlineUsers}
        currentUserId={currentUserId}
        classes={classes}
        cx={cx}
      />
      <QuickReplyPlugin quickReplies={quickReplies} classes={classes} cx={cx} />
      <FloatingToolbarPlugin onSelectionRect={onSelectionRect} />
      <EditorRefPlugin apiRef={apiRef} onRegister={registerComposer} />
    </LexicalComposer>
  );
}
